'use client';

import React, { FC, useState, useEffect, useCallback } from 'react';

// @mui
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Container from '@mui/material/Container';
// routes
// import { PATH_DASHBOARD as paths } from 'src/routes/paths';
// _mock
// import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';

import { useRouter } from 'next/navigation';

import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';

// components
import Iconify from 'src/components/iconify';
import { useSettingsContext } from 'src/components/settings';

//
import AccountGeneral from '../account-general';
import AccountBilling from '../account-billing';
import { useTranslate } from '../../../../../locales';
import AccountSocialLinks from '../account-social-links';
import { useGetMerchant } from '../../../../../api/shop';
import { getMerchant } from '../../../../../api/merchant';
import { IMerchant } from '../../../../../types/merchant';
import { useAuthContext } from '../../../../../auth/hooks';
import AccountNotifications from '../account-notifications';
import AccountChangePassword from '../account-change-password';
import { TeamCards } from '../../../../../components/team-cards';
import { useResponsive } from '../../../../../hooks/use-responsive';
import { getCustomerByPhoneOrMail } from '../../../../../api/customers';
import { ComponentTypeProps } from '../../../../../types/page-generator';
import { ICustomerItem } from '../../../../../types/customer-citypeople';
import { ActionTypes, useAppContext } from '../../../../../contexts/AppContext';
import {
  _userAbout,
  _userPlans,
  _userPayment,
  _userInvoices,
  _userAddressBook,
} from '../../../../../_mock';

// ----------------------------------------------------------------------

export type AccountTab = {
  value: string;
  label: string;
  icon: JSX.Element;
};

const TABS: AccountTab[] = [
  {
    value: 'general',
    label: 'General',
    icon: <Iconify icon="solar:user-id-bold" width={24} />,
  },
  {
    value: 'billing',
    label: 'Sales',
    icon: <Iconify icon="solar:bill-list-bold" width={24} />,
  },
  {
    value: 'notifications',
    label: 'Notifications',
    icon: <Iconify icon="solar:bell-bing-bold" width={24} />,
  },
  {
    value: 'social',
    label: 'Adds',
    icon: <Iconify icon="solar:share-bold" width={24} />,
  },
  {
    value: 'security',
    label: 'Security',
    icon: <Iconify icon="ic:round-vpn-key" width={24} />,
  },
  {
    value: 'team',
    label: 'Team',
    icon: <Iconify icon="icon-park-solid:family" width={24} />,
  },
];

// ----------------------------------------------------------------------

const BusinessAccountView: FC<ComponentTypeProps> = ({ block, params }: ComponentTypeProps) => {
  const sectionType = params.section.sectionType;
  const { user } = useAuthContext();
  const { dispatch, state } = useAppContext();
  const [customer, setCustomer] = useState<ICustomerItem[] | any>([]);
  const [currentMerchant, setCurrentMerchant] = useState<IMerchant | any>();
  const [selectedMerchantId, setSelectedMerchantId] = useState<number | null>(null);
  const merchantId = typeof window !== 'undefined' ? localStorage.getItem('merchantId') : '';
  const settings = useSettingsContext();
  const { t } = useTranslate();
  const [currentTab, setCurrentTab] = useState('general');
  const router = useRouter();
  const { logout } = useAuthContext();
  const isMobile = useResponsive('down', 'sm');
  const localMerchantId = state?.smbAccount?.merchant?.id;
  const { merchant } = useGetMerchant({ id: localMerchantId });

  useEffect(() => {
    // Если есть merchantId и selectedMerchantId ещё не задан, устанавливаем его
    // if (merchantId && !selectedMerchantId) {
    //   setSelectedMerchantId(Number(merchantId)); // Преобразуем merchantId в число
    // }

    // Если selectedMerchantId задан, загружаем данные мерчанта
    if (selectedMerchantId) {
      getMerchant(selectedMerchantId, dispatch).then((response) => {
        setCurrentMerchant(response); // Устанавливаем данные текущего мерчанта
      });
    }
  }, [merchantId, selectedMerchantId, dispatch]);

  const generalTabItem =
    block && Object.prototype.hasOwnProperty.call(block, 'generalTabItem')
      ? block.generalTabItem
      : true;
  const billingTabItem =
    block && Object.prototype.hasOwnProperty.call(block, 'billingTabItem')
      ? block.billingTabItem
      : true;
  const notificationsTabItem =
    block && Object.prototype.hasOwnProperty.call(block, 'notificationsTabItem')
      ? block.notificationsTabItem
      : true;
  const socialTabItem =
    block && Object.prototype.hasOwnProperty.call(block, 'socialTabItem')
      ? block.socialTabItem
      : true;
  const securityTabItem =
    block && Object.prototype.hasOwnProperty.call(block, 'securityTabItem')
      ? block.securityTabItem
      : true;
  const familyTabItem = Object.prototype.hasOwnProperty.call(block, 'familyTabItem')
    ? block?.familyTabItem
    : true;

  const activeTabNames: string[] = [];

  if (generalTabItem) activeTabNames.push('general');
  if (billingTabItem) activeTabNames.push('billing');
  if (notificationsTabItem) activeTabNames.push('notifications');
  if (socialTabItem) activeTabNames.push('social');
  if (securityTabItem) activeTabNames.push('security');
  if (familyTabItem) activeTabNames.push('team');

  const handleChangeTab = useCallback((event: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
  }, []);

  const logOut = () => {
    logout();
    router.push('/');
  };

  const setActiveDialog = (payload: null | string) => {
    dispatch({ type: ActionTypes.SET_ACTIVE_DIALOG, payload });
  };

  useEffect(() => {
    if (user) {
      getCustomerByPhoneOrMail(user.phone, user.email, dispatch).then((response: any) => {
        if (response) {
          setCustomer(response[0]);
        }
      });
    }
  }, []);

  const handleMerchantChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedValue = Number(event.target.value); // Преобразуем значение в число
    setSelectedMerchantId(selectedValue); // Сохраняем id в состояние

    // Загрузка данных для выбранного мерчанта
    getMerchant(selectedValue, dispatch).then((response) => {
      setCurrentMerchant(response); // Устанавливаем данные текущего мерчанта
    });
  };

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <Stack direction="row">
        <Tabs
          value={currentTab}
          onChange={handleChangeTab}
          sx={{
            mb: { xs: 3, md: 5 },
            '& .MuiTabs-scrollButtons': {
              width: { xs: '10px' },
              margin: { xs: '5px' },
            },
          }}
        >
          {TABS.filter((tab: AccountTab) => activeTabNames.includes(tab.value)).map(
            (tab: AccountTab) => (
              <Tab
                key={tab.value}
                label={`${t(`${tab.label}`)}`}
                icon={tab.icon}
                value={tab.value}
              />
            )
          )}
        </Tabs>
        {!isMobile && (
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={logOut}
            sx={{ mx: 2, mt: 1 }}
          >
            להתנתק
          </Button>
        )}
      </Stack>
      {isMobile && (
        <Button
          variant="contained"
          color="error"
          size="small"
          onClick={logOut}
          sx={{ mx: 2, mt: 1 }}
        >
          להתנתק
        </Button>
      )}
      {((currentTab === 'general' && generalTabItem) ||
        (currentTab === 'billing' && billingTabItem)) && (
        <TextField
          select
          fullWidth
          sx={{ mt: 1 }}
          label="Merchant"
          value={selectedMerchantId || ''} // Текущее значение мерчанта
          onChange={handleMerchantChange} // Обработчик изменения
        >
          {customer &&
            customer.merchants &&
            customer.merchants.map((option: IMerchant) => (
              <MenuItem
                key={option.id}
                value={option.id} // Значение для выбора
                sx={{
                  mx: 1,
                  borderRadius: 0.75,
                  typography: 'body2',
                  textTransform: 'capitalize',
                }}
              >
                {option.title} {/* Имя мерчанта */}
              </MenuItem>
            ))}
        </TextField>
      )}
      {currentTab === 'general' && generalTabItem && (
        <AccountGeneral
          socialLinks={_userAbout.socialLinks}
          currentMerchant={currentMerchant}
          block={block}
          selectedMerchantId={selectedMerchantId}
          setSelectedMerchantId={setSelectedMerchantId}
        />
      )}

      {currentTab === 'billing' && billingTabItem && (
        <AccountBilling
          plans={_userPlans}
          cards={_userPayment}
          // @ts-ignore
          invoices={_userInvoices}
          // @ts-ignore
          addressBook={_userAddressBook}
          block={block}
          selectedMerchantId={selectedMerchantId}
        />
      )}

      {currentTab === 'notifications' && notificationsTabItem && <AccountNotifications />}

      {currentTab === 'social' && socialTabItem && (
        <AccountSocialLinks socialLinks={_userAbout.socialLinks} />
      )}

      {currentTab === 'security' && securityTabItem && <AccountChangePassword />}

      {currentTab === 'team' && familyTabItem && (
        <TeamCards block={block} customer={customer} sectionType={sectionType} />
      )}
    </Container>
  );
};

export default BusinessAccountView;
